/* Mobelglede FAQ Tabs v2 (premium)
 * Custom element <faq-tabs>. Vanilla, no dependencies.
 * - Animated underline indicator that slides between active tabs
 * - View Transitions API for tab swap (progressive enhancement)
 * - Cross-fade panel content (180ms out, 220ms in)
 * - Hash deep-link with scroll-then-reveal choreography
 * - Live prefers-reduced-motion listener
 * - Pattern: WAI-ARIA tablist + roving tabindex + native <details>
 */

(function () {
  if (window.customElements && window.customElements.get('faq-tabs')) return;

  const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const DUR_OUT = 140;
  const DUR_IN = 220;

  class FaqTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.tabStrip = this.querySelector('.mg-faq__tabs');
      this.tabsWrap = this.querySelector('.mg-faq__tabs-wrap');
      this.indicator = this.querySelector('.mg-faq__indicator');
      this.navPrev = this.querySelector('.mg-faq__nav--prev');
      this.navNext = this.querySelector('.mg-faq__nav--next');

      if (!this.tabs.length || !this.panels.length) return;

      if (this.navPrev) {
        this.navPrev.removeAttribute('hidden');
        this.navPrev.addEventListener('click', () => this.scrollTabsBy(-1));
      }
      if (this.navNext) {
        this.navNext.removeAttribute('hidden');
        this.navNext.addEventListener('click', () => this.scrollTabsBy(1));
      }

      // Live reduced-motion listener
      this.rmQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reduceMotion = this.rmQuery.matches;
      this.rmHandler = (e) => { this.reduceMotion = e.matches; };
      if (this.rmQuery.addEventListener) {
        this.rmQuery.addEventListener('change', this.rmHandler);
      }

      this.tabs.forEach((tab, idx) => {
        tab.addEventListener('click', () => this.activate(idx));
        tab.addEventListener('keydown', (e) => this.handleKey(e, idx));
      });

      window.addEventListener('hashchange', () => this.openFromHash());

      const opened = this.openFromHash();
      if (!opened) {
        const initialIdx = Math.max(0, this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));
        this.activate(initialIdx, { updateHash: false, scrollIntoView: true, focus: false, animate: false });
      }

      if (this.tabStrip && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateScrollState();
          this.moveIndicator(this.activeTab(), { animate: false });
        });
        this.resizeObserver.observe(this.tabStrip);
      }

      if (this.tabStrip) {
        this.scrollHandler = () => {
          this.updateScrollState();
          this.moveIndicator(this.activeTab(), { animate: false });
        };
        this.tabStrip.addEventListener('scroll', this.scrollHandler, { passive: true });
      }

      this.updateScrollState();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.tabStrip && this.scrollHandler) this.tabStrip.removeEventListener('scroll', this.scrollHandler);
      if (this.rmQuery && this.rmQuery.removeEventListener) this.rmQuery.removeEventListener('change', this.rmHandler);
    }

    activeTab() {
      return this.tabs.find((t) => t.getAttribute('aria-selected') === 'true') || this.tabs[0];
    }

    activate(idx, opts) {
      opts = opts || {};
      const updateHash = opts.updateHash !== false;
      const scrollIntoView = opts.scrollIntoView !== false;
      const focus = opts.focus !== false;
      const animate = opts.animate !== false && !this.reduceMotion;
      const currentIdx = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');

      if (idx < 0 || idx >= this.tabs.length) return;
      if (idx === currentIdx && opts.fromInit !== true) {
        // Already active, but re-sync indicator in case of resize
        this.moveIndicator(this.tabs[idx], { animate: false });
        return;
      }

      const run = () => {
        const outgoing = currentIdx > -1 ? this.panels[currentIdx] : null;
        const incoming = this.panels[idx];

        this.tabs.forEach((tab, k) => {
          const isActive = k === idx;
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.setAttribute('tabindex', isActive ? '0' : '-1');
          tab.classList.toggle('is-active', isActive);
        });

        this.moveIndicator(this.tabs[idx], { animate });

        if (!animate || !outgoing || outgoing === incoming) {
          if (outgoing && outgoing !== incoming) outgoing.setAttribute('hidden', '');
          incoming.removeAttribute('hidden');
          incoming.style.opacity = '';
          incoming.style.transform = '';
          incoming.style.transition = '';
        } else {
          // Cross-fade: fade out current, then fade in next
          outgoing.style.transition = `opacity ${DUR_OUT}ms ${EASE}`;
          outgoing.style.opacity = '0';
          window.setTimeout(() => {
            outgoing.setAttribute('hidden', '');
            outgoing.style.opacity = '';
            outgoing.style.transition = '';

            incoming.removeAttribute('hidden');
            incoming.style.opacity = '0';
            incoming.style.transform = 'translateY(6px)';
            incoming.style.transition = 'none';

            requestAnimationFrame(() => {
              incoming.style.transition = `opacity ${DUR_IN}ms ${EASE}, transform ${DUR_IN}ms ${EASE}`;
              incoming.style.opacity = '1';
              incoming.style.transform = 'translateY(0)';

              window.setTimeout(() => {
                incoming.style.opacity = '';
                incoming.style.transform = '';
                incoming.style.transition = '';
              }, DUR_IN + 40);
            });
          }, DUR_OUT);
        }

        if (focus) {
          this.tabs[idx].focus({ preventScroll: true });
        }

        if (scrollIntoView && this.tabs[idx].scrollIntoView) {
          this.tabs[idx].scrollIntoView({
            inline: 'center',
            block: 'nearest',
            behavior: this.reduceMotion ? 'auto' : 'smooth'
          });
        }

        if (updateHash) {
          const slug = this.tabs[idx].dataset.slug;
          if (slug && history.replaceState) {
            history.replaceState(null, '', '#' + slug);
          }
        }
      };

      // View Transitions API as progressive enhancement
      if (animate && document.startViewTransition && currentIdx > -1) {
        try {
          document.startViewTransition(run);
        } catch (e) {
          run();
        }
      } else {
        run();
      }
    }

    moveIndicator(tab, opts) {
      if (!this.indicator || !this.tabStrip || !tab) return;
      const animate = opts && opts.animate;
      const stripBox = this.tabStrip.getBoundingClientRect();
      const tabBox = tab.getBoundingClientRect();
      const x = tabBox.left - stripBox.left + this.tabStrip.scrollLeft;
      const w = tabBox.width;
      const apply = () => {
        this.indicator.style.transition = animate
          ? `transform ${DUR_IN + 60}ms ${EASE}, width ${DUR_IN + 60}ms ${EASE}`
          : 'none';
        this.indicator.style.transform = `translateX(${x}px)`;
        this.indicator.style.width = w + 'px';
      };
      requestAnimationFrame(apply);
    }

    handleKey(e, idx) {
      const last = this.tabs.length - 1;
      let nextIdx = null;

      switch (e.key) {
        case 'ArrowRight':
          nextIdx = idx === last ? 0 : idx + 1;
          break;
        case 'ArrowLeft':
          nextIdx = idx === 0 ? last : idx - 1;
          break;
        case 'Home':
          nextIdx = 0;
          break;
        case 'End':
          nextIdx = last;
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          this.activate(idx);
          return;
        default:
          return;
      }

      e.preventDefault();
      this.activate(nextIdx);
    }

    openFromHash() {
      const hash = window.location.hash.slice(1);
      if (!hash) return false;

      const tabIdx = this.tabs.findIndex((t) => t.dataset.slug === hash);
      if (tabIdx > -1) {
        this.activate(tabIdx, { updateHash: false, scrollIntoView: true, focus: false });
        return true;
      }

      const cssEscape = window.CSS && CSS.escape ? CSS.escape(hash) : hash.replace(/[^a-zA-Z0-9_-]/g, '');
      const details = this.querySelector('details[data-slug="' + cssEscape + '"]');
      if (details) {
        const panel = details.closest('[role="tabpanel"]');
        const panelIdx = this.panels.indexOf(panel);
        if (panelIdx > -1) {
          this.activate(panelIdx, { updateHash: false, scrollIntoView: false, focus: false, animate: false });
        }
        this.revealDetails(details);
        return true;
      }
      return false;
    }

    revealDetails(details) {
      if (this.reduceMotion) {
        details.open = true;
        details.scrollIntoView({ block: 'center', behavior: 'auto' });
        return;
      }
      details.open = false;
      details.scrollIntoView({ block: 'center', behavior: 'smooth' });
      let done = false;
      const finish = () => { if (done) return; done = true; details.open = true; };
      if ('onscrollend' in window) {
        window.addEventListener('scrollend', finish, { once: true });
      }
      window.setTimeout(finish, 600);
    }

    updateScrollState() {
      if (!this.tabStrip || !this.tabsWrap) return;
      const sl = this.tabStrip.scrollLeft;
      const sw = this.tabStrip.scrollWidth;
      const cw = this.tabStrip.clientWidth;
      const canPrev = sl > 2;
      const canNext = sl + cw < sw - 2;
      this.tabsWrap.dataset.canScrollLeft = canPrev ? 'true' : 'false';
      this.tabsWrap.dataset.canScrollRight = canNext ? 'true' : 'false';
    }

    scrollTabsBy(direction) {
      if (!this.tabStrip) return;
      const amount = Math.max(160, this.tabStrip.clientWidth * 0.6);
      this.tabStrip.scrollBy({
        left: direction * amount,
        behavior: this.reduceMotion ? 'auto' : 'smooth'
      });
    }
  }

  customElements.define('faq-tabs', FaqTabs);

  // Mark <html> as JS-enabled for CSS hooks (no-JS fallback)
  if (!document.documentElement.classList.contains('js')) {
    document.documentElement.classList.add('js');
  }
})();
